/**
 * 机构与校区 API（org-campus change 2-1）。
 *
 * 对齐后端 org-campus change 的 REST 契约：
 * - GET /organizations/current -> Organization
 * - PATCH /organizations/current -> Organization（partial 更新）
 * - GET /campuses -> Paginated<CampusItem>（筛选 + 分页）
 * - GET /campuses/:id -> CampusDetail
 * - POST /campuses -> CampusDetail（新建）
 * - PATCH /campuses/:id -> CampusDetail（编辑）
 * - POST /campuses/:id/suspend -> CampusDetail（停用）
 * - POST /campuses/:id/activate -> CampusDetail（启用）
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 机构信息（org-campus:SC-01）。 */
export interface Organization {
  id: string;
  name: string;
  status: string;
  defaultTimezone: string;
  defaultProvince: string;
  defaultCity: string;
}

/** 更新机构请求体（partial）。 */
export type UpdateOrganization = Partial<
  Pick<Organization, 'name' | 'defaultTimezone' | 'defaultProvince' | 'defaultCity'>
>;

/** 校区状态。 */
export type CampusStatus = 'ACTIVE' | 'SUSPENDED';

/** 校区视图（列表项与详情共用，含统计占位字段）。 */
export interface CampusItem {
  id: string;
  organizationId: string;
  code: string;
  /** 校区数字码（4 位纯数字，学号前 4 位前缀，创建后不可修改） */
  numericCode: string;
  name: string;
  address?: string | null;
  timezone: string;
  status: CampusStatus;
  version: number;
  /** 员工数（一期占位 0） */
  staffCount: number;
  /** 在读学生数（一期占位 0） */
  studentCount: number;
  /** 进行中班级数（一期占位 0） */
  activeClassCount: number;
}

/** 分页响应。 */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** 校区列表查询参数。 */
export interface CampusListQuery {
  name?: string;
  code?: string;
  status?: CampusStatus;
  page?: number;
  pageSize?: number;
}

/** 新建校区请求体。 */
export interface CreateCampus {
  code: string;
  /** 校区数字码（4 位纯数字，机构内唯一，创建后不可修改） */
  numericCode: string;
  name: string;
  address?: string;
  timezone?: string;
}

/** 编辑校区请求体（含乐观锁 version）。 */
export interface UpdateCampus {
  name?: string;
  address?: string;
  timezone?: string;
  version: number;
}

async function getCurrentOrg(): Promise<Organization> {
  return http.get<Organization>(ApiPaths.organizations.current);
}

async function updateCurrentOrg(
  patch: UpdateOrganization,
): Promise<Organization> {
  return http.patch<Organization>(ApiPaths.organizations.update, patch);
}

function buildListQuery(query: CampusListQuery): string {
  const params = new URLSearchParams();
  if (query.name) params.set('name', query.name);
  if (query.code) params.set('code', query.code);
  if (query.status) params.set('status', query.status);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  return qs ? `${ApiPaths.campuses.list}?${qs}` : ApiPaths.campuses.list;
}

async function listCampuses(
  query: CampusListQuery = {},
): Promise<Paginated<CampusItem>> {
  return http.get<Paginated<CampusItem>>(buildListQuery(query));
}

async function getCampus(id: string): Promise<CampusItem> {
  return http.get<CampusItem>(ApiPaths.campuses.detail(id));
}

async function createCampus(dto: CreateCampus): Promise<CampusItem> {
  return http.post<CampusItem>(ApiPaths.campuses.create, dto);
}

async function updateCampus(
  id: string,
  dto: UpdateCampus,
): Promise<CampusItem> {
  return http.patch<CampusItem>(ApiPaths.campuses.update(id), dto);
}

async function suspendCampus(id: string): Promise<CampusItem> {
  return http.post<CampusItem>(ApiPaths.campuses.suspend(id), {});
}

async function activateCampus(id: string): Promise<CampusItem> {
  return http.post<CampusItem>(ApiPaths.campuses.activate(id), {});
}

/** 机构与校区 API 唯一对外入口。 */
export const orgCampusApi = {
  getCurrentOrg,
  updateCurrentOrg,
  listCampuses,
  getCampus,
  createCampus,
  updateCampus,
  suspendCampus,
  activateCampus,
};
